import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import './notifications.css';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import {
  Form,
  FormField,
  FormItem,
  FormControlWithFocus,
  useFormValidation,
} from '../../../../shared/components/form';
import { createNotificationPreferencesSchema } from '../../../../shared/validations/setting';
import {
  fetchUser,
  updateUser as updateUserAction,
} from '../../../../shared/actions/userActions';
import { ROLE } from '../../../../shared/constants/common';
import { ROUTES } from '../../../../shared/constants/routes';
import { getUserRole, isUserLoggedIn } from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../../shared/pusNotification/useManualToasts';

const ModernNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const isLoggedIn = isUserLoggedIn();
  const userRole = useMemo(() => getUserRole(), []);
  const shouldRedirectToCreatorNotifications =
    userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;

  const {
    user: userProfile,
    loading: isUserLoading,
  } = useSelector((state) => state.user || {});
  const schema = useMemo(() => createNotificationPreferencesSchema(), []);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const defaultValues = useMemo(
    () => ({
      notifications: {
        payment: true,
        messages: true,
      },
    }),
    []
  );

  const form = useForm({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { validateAndFocus } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const [initialValues, setInitialValues] = useState(defaultValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentValue = form.watch('notifications.payment');
  const messageValue = form.watch('notifications.messages');

  const hasChanges = useMemo(() => {
    return (
      paymentValue !== initialValues.notifications.payment ||
      messageValue !== initialValues.notifications.messages
    );
  }, [initialValues, messageValue, paymentValue]);

  const registeredEmail = useMemo(() => {
    return userProfile?.email || '';
  }, [userProfile]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate.toSignup();
      return;
    }

    if (shouldRedirectToCreatorNotifications) {
      navigate.to(ROUTES.CREATOR_NOTIFICATIONS, { replace: true });
    }
  }, [isLoggedIn, navigate, shouldRedirectToCreatorNotifications]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    if (!userProfile && !isUserLoading) {
      dispatch(fetchUser());
    }
  }, [dispatch, isLoggedIn, isUserLoading, userProfile]);
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const notificationsSource = userProfile?.notifications;

    const nextValues = {
      notifications: {
        payment: notificationsSource?.payment !== false,
        messages: notificationsSource?.messages !== false,
      },
    };

    setInitialValues((prev) => {
      const hasPreferenceChanged =
        prev.notifications.payment !== nextValues.notifications.payment ||
        prev.notifications.messages !== nextValues.notifications.messages;

      if (hasPreferenceChanged) {
        form.reset(nextValues, { keepDirty: false });
        return nextValues;
      }

      return prev;
    });
  }, [form, isLoggedIn, userProfile]);

  const isSaveDisabled =
    isSubmitting || form.formState.isSubmitting || !hasChanges;

  const handleSavePreferences = useCallback(
    async (values) => {
      setIsSubmitting(true);

      try {
        const payload = {
          notifications: {
            master: true,
            payment: values.notifications.payment,
            messages: values.notifications.messages,
          },
        };

        const result = await dispatch(updateUserAction(payload));

        if (!result?.success) {
          throw new Error(
            result?.error || result?.message || 'Failed to save notification preferences.'
          );
        }

        await dispatch(fetchUser());

        const normalizedValues = {
          notifications: {
            payment: values.notifications.payment,
            messages: values.notifications.messages,
          },
        };

        setInitialValues(normalizedValues);
        form.reset(normalizedValues, { keepDirty: false });

        localStorage.setItem(
          'send_notification',
          JSON.stringify(Boolean(values.notifications.payment || values.notifications.messages))
        );

        showManualSuccessPushToast(
          'Notifications Updated',
          'Your notification preferences have been saved successfully.',
          userProfile
        );
      } catch (error) {
        await SwalHandled.showError(
          'Error',
          error?.response?.data?.message ||
          error.message ||
          'Failed to save notification preferences. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, form]
  );

  const onSubmit = useCallback(
    async (values) => {
      const isValid = await validateAndFocus();

      if (!isValid) {
        return;
      }

      await handleSavePreferences(values);
    },
    [handleSavePreferences, validateAndFocus]
  );

  if (!isLoggedIn || shouldRedirectToCreatorNotifications) {
    return null;
  }

  return (
    <div className="modern-notifications-page">
      <ModernHeader
        customTitle="Notifications"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <main className="modern-notifications-main">
        <section className="modern-notifications-description">
          <p className="modern-notifications-description-text">
            Get notified when you are charged, receive a message from a creator and any updates from our platform.
          </p>
        </section>

        <section className="modern-notifications-email-section">
          <div className="modern-notifications-email-card">
            <h2 className="modern-notifications-email-title">Email Notifications</h2>
            <div className="modern-notifications-email-info">
              <div className="modern-notifications-email-details">
                <p className="modern-notifications-email-label">Registered Email</p>
                <p className="modern-notifications-email-value">
                  {registeredEmail || 'Not available'}
                </p>
              </div>
              <div className="modern-notifications-email-badge">
                Saved
              </div>
            </div>
            <p className="modern-notifications-email-note">
              Your registered email cannot be changed after signup.
            </p>
          </div>
        </section>

        <Form {...form}>
          <form
            className="modern-notifications-form"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <section className="modern-notifications-toggles-section">
              <div className="modern-notifications-toggles-card">
                <h2 className="modern-notifications-toggles-title">Notification Preferences</h2>

                <div className="modern-notifications-toggles-list">
                  <FormField
                    control={form.control}
                    name="notifications.payment"
                    render={({ field }) => (
                      <FormItem className="modern-notifications-toggle-item">
                        <div className="modern-notifications-toggle-info">
                          <p className="modern-notifications-toggle-name">Payment Notifications</p>
                          <p className="modern-notifications-toggle-description">
                            Get notified when you are charged.
                          </p>
                        </div>
                        <FormControlWithFocus fieldName="notifications.payment">
                          <label className="modern-notifications-toggle-switch">
                            <input
                              ref={field.ref}
                              type="checkbox"
                              className="modern-notifications-toggle-input"
                              checked={Boolean(field.value)}
                              onChange={(event) =>
                                field.onChange(event.target.checked)
                              }
                              onBlur={field.onBlur}
                              disabled={isSubmitting || isUserLoading}
                            />
                            <div className="modern-notifications-toggle-slider"></div>
                          </label>
                        </FormControlWithFocus>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notifications.messages"
                    render={({ field }) => (
                      <FormItem className="modern-notifications-toggle-item">
                        <div className="modern-notifications-toggle-info">
                          <p className="modern-notifications-toggle-name">Message Notifications</p>
                          <p className="modern-notifications-toggle-description">
                            Get notified when you receive a message or call request.
                          </p>
                        </div>
                        <FormControlWithFocus fieldName="notifications.messages">
                          <label className="modern-notifications-toggle-switch">
                            <input
                              ref={field.ref}
                              type="checkbox"
                              className="modern-notifications-toggle-input"
                              checked={Boolean(field.value)}
                              onChange={(event) =>
                                field.onChange(event.target.checked)
                              }
                              onBlur={field.onBlur}
                              disabled={isSubmitting || isUserLoading}
                            />
                            <div className="modern-notifications-toggle-slider"></div>
                          </label>
                        </FormControlWithFocus>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>

            <section className="modern-notifications-save-section">
              <button
                type="submit"
                className="modern-notifications-save-btn"
                disabled={isSaveDisabled}
              >
                {isSubmitting || form.formState.isSubmitting ? 'Saving...' : 'Save Preferences'}
              </button>
            </section>
          </form>
        </Form>
      </main>
    </div>
  );
};

export default ModernNotifications;